
import { mapGetters, mapActions } from "vuex";
import { isEqual } from "lodash";
import useProductMixin from "~/mixins/useProductMixin";
import useProductFilter from "~/mixins/useProductFilter";
import googleAnalyticsEvents from "~/mixins/googleAnalyticsEvents";
import facebookPixelEvents from "~/mixins/facebookPixelEvents";
import useEvents from "~/mixins/useEvents";
import { getProductImages, mergeImages, DataURIToBlob } from "~/utils";
import UiButton from "~/components/UI/UiButton.vue";
import UiSpinner from "~/components/UI/UiSpinner.vue";
import ProductMaterialSelectView from "~/components/product/ProductMaterialSelectView.vue";

export default {
  name: "CartDiscountProductCard",
  components: {
    UiButton,
    UiSpinner,
    ProductMaterialSelectView,
  },
  mixins: [
    useProductMixin,
    useProductFilter,
    googleAnalyticsEvents,
    facebookPixelEvents,
    useEvents,
  ],
  props: {
    product: {
      type: Object,
      required: true,
    },
    productIndex: {
      type: Number,
      default: 0,
    },
    statisticItemListName: {
      type: String,
      default: "",
    },
    statisticItemListId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      addingToCart: false,
      deleting: false,
      productData: null,
      useCartConfigurationEndpoint: true,
      configurationCartId: this.$store.getters.getCartId,
    };
  },
  computed: {
    ...mapGetters({
      getWishList: "wishlist/getWishList",
    }),
    productSKU() {
      const product = this.localProduct || this.product;
      return (
        product?.productSKU ||
        product?.selected_config?.configuration_id ||
        product?.variant_plp_image?.sku ||
        product?.default_config?.configuration_id ||
        product?.default_sku
      );
    },
    productImage() {
      const product = this.localProduct || this.product;
      const images = getProductImages(product);
      return images?.[0]?.url || "";
    },
    isInWishlist() {
      return this.getWishList.some((el) =>
        isEqual(
          {
            sku: el.sku,
            engraving: el.engraving,
          },
          { engraving: {}, sku: this.productSKU }
        )
      );
    },
    currentConfigurationPrice() {
      // Always use variant_plp_image.cost as it's always updated correctly
      // productData.cost may contain stale value when new cost is 0 (due to falsy check in useProductFilter)
      return Number(this.productData?.variant_plp_image?.cost || 0);
    },
    productPrice() {
      // Override mixin's productPrice to use only current configuration data
      // Mixin's fallback to base_cost/min_cost can return stale values when cost is 0
      return this.currentConfigurationPrice;
    },
    isDiscountProductOutOfStock() {
      // Check if price is invalid (0 or missing)
      const hasInvalidPrice = !this.currentConfigurationPrice || this.currentConfigurationPrice === 0;

      // Check inventory status from mixin
      const isOutOfStock = this.isConfigurationInventoryOutOfStockPlp;

      return hasInvalidPrice || isOutOfStock;
    },
  },
  watch: {
    product: {
      handler(val) {
        if (val) {
          this.productData = val;

          const sku = val?.variant_plp_image?.sku || val?.default_sku || val?.productSKU;
          this.localProduct = {
            ...val,
            productSKU: sku,
            default_sku: sku,
          };

          // Call setMaterials synchronously to set materials before child components mount
          this.setMaterials();
        }
      },
      immediate: true,
      deep: true,
    },
    localProduct: {
      handler(val) {
        if (val && val !== this.product) {
          this.productData = { ...val };
        }
      },
      deep: true,
    },
  },
  mounted() {
    if (this.product) {
      this.productData = this.product;

      if (!this.localProduct) {
        this.localProduct = { ...this.product };
      }
    }
  },
  methods: {
    ...mapActions({
      setWishlist: "wishlist/setWishlist",
    }),
    toggleWishlist() {
      this.onWishlist();
    },
    async onWishlist() {
      if (!this.product.default_sku) {
        return;
      }

      const payload = {
        value: {
          engraving: {},
          images: this.productImage,
          sku: this.productSKU,
        },
      };

      try {
        const wishlistUniqueId = this.$store.getters.getWishListId;
        let wishlist = JSON.parse(JSON.stringify(this.getWishList));
        if (!wishlist) {
          wishlist = [];
        }

        const isExist = wishlist.find((el) =>
          isEqual(
            { sku: el.sku, engraving: el.engraving },
            { engraving: payload.value.engraving, sku: payload.value.sku }
          )
        );

        const wishlistEventData = {
          product_price: this.productPrice,
          product_title: this.productTitle,
          product_id: this.product.id,
          configuration_id: this.productSKU,
          product_configuration_id: this.product?.default_config?.id,
        };

        if (!isExist) {
          wishlist.push({
            ...payload.value,
            slug: this.product.slug,
            title: this.productTitle,
            price: this.productPrice,
            embedded: this.product.embedded,
            translations: this.product.translations || [],
            vendor_model: this.product.vendor_model,
            id: this.product.id,
            collections_ids: this.product.collections_ids || [],
            start_at_cost: this.productPrice,
            product_id: this.product.id,
            configuration_id: this.productSKU,
            product_configuration_id: this.product?.default_config?.id,
          });

          this.gaAddToWishlistEvent(wishlistEventData);
          this.fbAddToWishlistEvent(wishlistEventData);
        } else {
          wishlist = wishlist.filter(
            (el) =>
              !isEqual(
                { sku: el.sku, engraving: el.engraving },
                { engraving: payload.value.engraving, sku: payload.value.sku }
              )
          );
          this.gaRemoveFromWishlistEvent(wishlistEventData);
          this.fbRemoveFromWishlistEvent(wishlistEventData);
        }

        const { data } = await this.$api.wishlist.addItem({
          unique_id: wishlistUniqueId,
          baggage: wishlist,
        });

        const eventPayload = {
          product_id: this.product.id,
          configuration_id: this.productSKU,
          product_sku: this.product.vendor_model,
          product_price: this.productPrice,
          collection_id: this.product.collections_ids?.[0] || "",
          product_configuration_id: this.product?.default_config?.id,
        };

        if (!isExist) {
          this.addToFavoritesEvent(eventPayload);
        } else {
          this.removeFromFavoritesEvent(eventPayload);
        }

        this.setWishlist(data.data.baggage);
      } catch (err) {}
    },
    sendStatistic() {
      this.gaSelectItemEvent(this.product, {
        index: this.productIndex,
        item_list_name: this.statisticItemListName,
        item_list_id: this.statisticItemListId,
      });
    },
    async addToCart() {
      if (this.addingToCart || !this.productSKU) {
        return;
      }
      this.addingToCart = true;
      try {
        const images = getProductImages(this.localProduct || this.product);
        const imageIdList =
          images?.filter((item) => item?.id).map((item) => item.id) || [];
        const imageId = imageIdList?.[0];

        let cartImage = null;
        if (!imageId) {
          cartImage = await mergeImages([
            {
              src: this.productImage || "/images/no-image.webp",
            },
          ]);
          cartImage = DataURIToBlob(cartImage);
        }

        const options = {};
        if (this.ringSizeSelectedItem && this.ringSizeMaterialCategory) {
          options.materials = [
            {
              sku: this.ringSizeSelectedItem.sku,
              title:
                this.ringSizeSelectedItem.mixedTranslation?.title ||
                this.ringSizeSelectedItem.title ||
                "",
              material_category_title:
                this.ringSizeMaterialCategory.mixedTranslation?.name || "",
              material_category_id: this.ringSizeMaterialCategory.id,
              material_category_sku: this.ringSizeMaterialCategory.sku,
            },
          ];
        }

        const currentProduct = this.localProduct || this.product;
        const formData = new FormData();
        formData.append("cart_unique_id", this.$store.getters.getCartId);
        formData.append("sku", this.productSKU);
        formData.append("vendor_model", currentProduct?.vendor_model || "");
        formData.append("quantity", "1");
        formData.append("options", JSON.stringify(options));

        if (imageId) {
          formData.append("image_id", imageId);
          imageIdList.forEach((id) => {
            formData.append("images_ids[]", id);
          });
        } else {
          formData.append("image", cartImage);
        }

        const { data } = await this.$api.cart.setCartItem(formData);
        await this.$store.dispatch("cart/setCartInfo", data.data);

        const configInfo = {
          configuration_id: this.productSKU,
          base_cost: this.productPrice,
        };

        this.gaAddToCartEvent(currentProduct, configInfo);
        this.fbAddToCartEvent(currentProduct, configInfo);
        this.addToCartEvent({
          product_id: currentProduct.id,
          configuration_id: this.productSKU,
          product_sku: currentProduct.vendor_model,
          product_price: this.productPrice,
        });

        // Notify parent component about product addition
        this.$emit("product-added");
      } catch (error) {
      } finally {
        this.addingToCart = false;
      }
    },
    async handleDelete() {
      if (this.deleting) {
        return;
      }

      this.deleting = true;
      try {
        const cartId = this.$store.getters.getCartId;
        await this.$api.cart.deleteCartDiscountProduct(cartId, this.product.id);

        // Notify parent component about product deletion
        this.$emit("product-deleted");
      } catch (error) {
        console.error("Error deleting discount product:", error);
      } finally {
        this.deleting = false;
      }
    },
  },
};
